import Head from "next/head";
import StoreLayout from "../layout/StoreLayout";
import "../styles/Report.css";
import "../styles/global.css";
import "../styles/satoshi.css";
import "../styles/galano-grotesque.css";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import "react-toastify/dist/ReactToastify.css";
import GoogleAnalytics from "../components/Analytics"; // Import the Google Analytics component
import Script from "next/script";
import { PopupProvider } from "../context/context";
import dynamic from "next/dynamic";
import { Toaster } from "react-hot-toast";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { GOOGLE_RECAPTCHA } from "../utilities/constant";
import { useRouter } from "next/router";
import { useEffect } from "react";

export function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const path = router.pathname;
  
  // Set HTML lang attribute programmatically
  useEffect(() => {
    document.documentElement.lang = 'en';
  }, []);
  
  // Track page views
  useEffect(() => {
    const handleRouteChange = (url) => {
      if (typeof window !== 'undefined') {
        if (window.gtag) {
          window.gtag('event', 'page_view', {
            page_path: url,
            page_title: document.title,
            page_location: window.location.href
          });
          console.log(`Tracked page view: ${url}`);
        } else if (window.trackEvent) {
          window.trackEvent('page_view', {
            page_path: url,
            page_title: document.title,
            page_location: window.location.href
          });
        }
      }
    };

    // Track initial page load
    handleRouteChange(router.asPath);

    // Track route changes
    router.events.on('routeChangeComplete', handleRouteChange);
    
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router]);
  
  // Add event listener for data-event attributes
  useEffect(() => {
    const handleClickEvents = (e) => {
      const target = e.target.closest('[data-event]');
      if (!target) return;
      
      const eventName = target.dataset.event;
      if (!eventName) return;
      
      const eventParams = {
        event_category: target.dataset.eventCategory || 'Interaction',
        event_label: target.dataset.eventLabel || '',
        value: target.dataset.eventValue ? Number(target.dataset.eventValue) : undefined
      };
      
      if (window.gtag) {
        window.gtag('event', eventName, eventParams);
        console.log(`Event tracked via data attribute: ${eventName}`, eventParams);
      } else if (window.trackEvent) {
        window.trackEvent(eventName, eventParams);
      }
    };
    
    document.addEventListener('click', handleClickEvents);
    
    return () => {
      document.removeEventListener('click', handleClickEvents);
    };
  }, []);
  
  // Only apply reCAPTCHA to sign-in and sign-up pages
  const isAuthPage = path === '/sign-in' || path === '/sign-up';
  
  // Conditionally render with or without reCAPTCHA provider
  const renderContent = () => {
    const content = (
      <PopupProvider>
        <StoreLayout>
          <Component {...pageProps} />
          <Toaster position="bottom-right" reverseOrder={false} />
        </StoreLayout>
      </PopupProvider>
    );
    
    // Only wrap with GoogleReCaptchaProvider if it's an auth page
    if (isAuthPage) {
      return (
        <GoogleReCaptchaProvider reCaptchaKey={GOOGLE_RECAPTCHA}>
          {content}
        </GoogleReCaptchaProvider>
      );
    }
    
    return content;
  };

  return (
    <>
      <Head>
        <meta 
          name="viewport" 
          content="width=device-width, initial-scale=1.0, viewport-fit=cover" 
        />
        <link
          href="https://api.fontshare.com/v2/css?f[]=satoshi@1,900,700,500,301,701,300,501,401,901,400,2&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap"
          rel="stylesheet"
        />
        
        {/* <Script src="https://accounts.google.com/gsi/client" async></Script> */}
        <link rel="icon" href="/favicon.ico" sizes="any" />
      </Head>
      <GoogleAnalytics />
      {/* Debug script for Google Analytics */}
      <Script 
        src="/index.js"
        strategy="afterInteractive"
      />
      {renderContent()}
    </>
  );
}

export default dynamic(() => Promise.resolve(MyApp), { ssr: false });
